import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CaseDocuments from "@/components/CaseDocuments";
import CaseMessages from "@/components/CaseMessages";
import CaseTreatments from "@/components/CaseTreatments";
import MyCaseAttorneyCard from "@/components/MyCaseAttorneyCard";
import ComplianceCard from "@/components/ComplianceCard";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, Calendar, MapPin, ChevronRight, Phone, Plus, LogOut, CheckCircle2 } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const STATUS_FLOW = ["new", "contacted", "qualified", "closed"];

function StatusBadge({ status, t }) {
  const label = t(`mc.status.${status}`);
  return (
    <span className={`status-${status} inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider`} data-testid={`case-status-${status}`}>
      {label}
    </span>
  );
}

function CaseTimeline({ status, t }) {
  if (status === "rejected") {
    return (
      <div className="rounded-lg border border-[#F5B6B0] bg-[#FDE8E6] px-4 py-3 text-sm text-[#B91C1C]">
        {t("mc.rejected_msg")}
      </div>
    );
  }
  const currentIdx = STATUS_FLOW.indexOf(status);
  return (
    <ol className="grid gap-3 sm:grid-cols-4" data-testid="case-timeline">
      {STATUS_FLOW.map((s, i) => {
        const done = i <= currentIdx;
        return (
          <li key={s} className="flex items-start gap-3">
            <div className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full border ${done ? "bg-[#11322A] border-[#11322A] text-white" : "bg-white border-[#E0E4E2] text-[#5C6B68]"}`}>
              {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="text-xs font-bold">{i + 1}</span>}
            </div>
            <div>
              <div className={`text-xs font-bold uppercase tracking-[0.12em] ${done ? "text-[#11322A]" : "text-[#5C6B68]"}`}>{t(`mc.status.${s}`)}</div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function MyCases() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get("/me/cases")
      .then((r) => setCases(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen" data-testid="my-cases-page">
      <Navbar />
      <section className="mx-auto max-w-6xl px-6 py-16 lg:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="label-cap">{t("mc.label")}</span>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight text-[#11322A] sm:text-4xl">
              {t("mc.welcome", { name: user?.name?.split(" ")[0] || (t("lang.spanish") === "Español" ? "amigo" : "there") })}
            </h1>
            <p className="mt-2 text-[#5C6B68]">{t("mc.subtitle")}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/intake" className="btn-primary text-sm" data-testid="mycases-new">
              <Plus className="h-4 w-4" /> {t("mc.new_case")}
            </Link>
            <button onClick={doLogout} className="inline-flex items-center gap-1.5 rounded-lg border border-[#E0E4E2] bg-white px-3 py-2 text-sm font-bold text-[#0B1317] hover:bg-[#FDFBF7]" data-testid="mycases-logout">
              <LogOut className="h-3.5 w-3.5" /> {t("mc.logout")}
            </button>
          </div>
        </div>

        <div className="mt-10">
          {loading && <div className="card-soft p-12 text-center text-[#5C6B68]">{t("mc.loading")}</div>}

          {!loading && cases.length === 0 && (
            <div className="card-soft p-12 text-center" data-testid="mycases-empty">
              <FileText className="mx-auto h-8 w-8 text-[#5C6B68]" />
              <h2 className="mt-4 font-display text-xl font-bold text-[#0B1317]">{t("mc.empty.title")}</h2>
              <p className="mt-2 text-sm text-[#5C6B68]">{t("mc.empty.body")}</p>
              <Link to="/intake" className="btn-primary mt-6 inline-flex" data-testid="mycases-cta">
                {t("mc.empty.cta")}
              </Link>
            </div>
          )}

          {!loading && cases.length > 0 && (
            <div className="grid gap-4">
              {cases.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className="card-soft group p-6 text-left transition hover:border-[#11322A]"
                  data-testid={`case-card-${c.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-lg font-bold text-[#0B1317]">
                        {c.accident_type || t("mc.fallback_title")}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5C6B68]">
                        <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{c.state}{c.city ? ` · ${c.city}` : ""}</span>
                        {c.accident_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{c.accident_date}</span>}
                        <span>{t("mc.detail.submitted")} {new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={c.status} t={t} />
                      <ChevronRight className="h-4 w-4 text-[#5C6B68] transition group-hover:translate-x-0.5 group-hover:text-[#E85D04]" />
                    </div>
                  </div>
                  <div className="mt-6">
                    <CaseTimeline status={c.status} t={t} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="case-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black tracking-tight text-[#11322A]">
                  {selected.accident_type || t("mc.fallback_title")}
                </DialogTitle>
                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <StatusBadge status={selected.status} t={t} />
                  <span className="pill"><MapPin className="h-3 w-3" /> {selected.state}{selected.city ? ` · ${selected.city}` : ""}</span>
                  {selected.accident_date && <span className="pill"><Calendar className="h-3 w-3" /> {selected.accident_date}</span>}
                </div>
              </DialogHeader>

              <Tabs defaultValue="overview" className="mt-4">
                <TabsList className="w-full justify-start" data-testid="case-tabs">
                  <TabsTrigger value="overview" data-testid="tab-overview">{t("mc.tab.overview")}</TabsTrigger>
                  <TabsTrigger value="treatments" data-testid="tab-treatments">{t("mc.tab.treatments")}</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="tab-documents">{t("mc.tab.documents")}</TabsTrigger>
                  <TabsTrigger value="messages" data-testid="tab-messages">{t("mc.tab.messages")}</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-4">
                  <MyCaseAttorneyCard caseId={selected.id} />
                  <div className="mt-4">
                    <ComplianceCard caseId={selected.id} variant="claimant" />
                  </div>
                  <div className="mt-4">
                    <CaseTimeline status={selected.status} t={t} />
                  </div>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2 text-sm">
                    <div><div className="label-cap text-[10px]">{t("mc.detail.submitted")}</div><div className="mt-1">{new Date(selected.created_at).toLocaleString()}</div></div>
                    <div><div className="label-cap text-[10px]">{t("mc.detail.fault")}</div><div className="mt-1">{selected.at_fault || "—"}</div></div>
                    <div className="sm:col-span-2"><div className="label-cap text-[10px]">{t("mc.detail.injuries")}</div><div className="mt-1 text-[#0B1317]">{selected.injuries || "—"}</div></div>
                    {selected.description && (
                      <div className="sm:col-span-2"><div className="label-cap text-[10px]">{t("mc.detail.notes")}</div><div className="mt-1 whitespace-pre-wrap text-[#0B1317]">{selected.description}</div></div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="treatments" className="mt-4">
                  <CaseTreatments caseId={selected.id} scope="claimant" />
                </TabsContent>

                <TabsContent value="documents" className="mt-4">
                  <CaseDocuments caseId={selected.id} scope="claimant" />
                </TabsContent>

                <TabsContent value="messages" className="mt-4">
                  <CaseMessages caseId={selected.id} scope="claimant" />
                </TabsContent>
              </Tabs>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#E0E4E2] pt-4">
                <a href="tel:+18005551234" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#11322A] hover:text-[#E85D04]" data-testid="case-call">
                  <Phone className="h-3.5 w-3.5" /> {t("mc.detail.call")}
                </a>
                <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1.5 rounded-lg bg-[#11322A] px-3 py-2 text-sm font-bold text-white hover:bg-[#0a221c]" data-testid="case-close">
                  {t("close")}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
